// Feature flag: when true, the app reads chart data from the Postgres chart-data
// API (/api/chart-data/*) instead of Google Sheets/Drive. Off by default so prod
// stays on Sheets; enabled on dev via REACT_APP_USE_CHART_DB=true. Migration is
// rolled out one slice at a time behind this flag.
export const USE_CHART_DB = process.env.REACT_APP_USE_CHART_DB === 'true';

// Server configuration - uses environment variables for deployment flexibility
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
export const SERVER_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';
export const FRONTEND_BASE_URL = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';

// Admin allowlist for the billing dashboard. Client-side gating is cosmetic
// (nav visibility + route redirect); the server's /api/admin routes enforce
// the same list and 403 everyone else.
export const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || 'scott@channel1.ai,adam@channel1.ai')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

// Master Video List fallback ID - used when context discovery fails
// The Master Video List ID is normally discovered dynamically from the root folder.
// This fallback is only used if discovery fails (e.g., no root folder configured).
// DEV: 1OfZUlMDK_b_N0n8chwIbPVKE6yPFp5IclQTT9Hq7cJw
// PROD: 1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo
export const MASTER_VIDEO_LIST_FALLBACK_ID = '1OfZUlMDK_b_N0n8chwIbPVKE6yPFp5IclQTT9Hq7cJw';

// Chart Category - default "Hot 100", stored in localStorage for future UI picker
const CHART_CATEGORY_STORAGE_KEY = 'billboardChartCategory';
const DEFAULT_CHART_CATEGORY = 'Hot 100';

export const getChartCategory = (): string => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(CHART_CATEGORY_STORAGE_KEY) || DEFAULT_CHART_CATEGORY;
  }
  return DEFAULT_CHART_CATEGORY;
};

export const setChartCategory = (category: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(CHART_CATEGORY_STORAGE_KEY, category);
  }
};

// Cache keys for chart config discovery
export const CHART_CONFIG_CACHE_KEYS = {
  CHART_CONFIG_SHEET_ID: 'billboard_chartConfigSheetId',
  CHART_CONFIG: 'billboard_chartConfig',
};

// VO language per weekly sheet (bilingual charts: EN + ES). It's a per-sheet UI
// selector — which language's VO the editor/export operates on — not chart data,
// so it lives in localStorage keyed by the sheet id (mirrors lineAudioFiles_{id}).
// Default 'en'; shared data (positions/graphics/settings) is identical in any language.
export type VoLanguage = 'en' | 'es';
const VO_LANGUAGE_KEY = (sheetId: string) => `billboardVoLanguage_${sheetId}`;
export const getVoLanguage = (sheetId: string | null | undefined): VoLanguage => {
  if (typeof window !== 'undefined' && sheetId) {
    const v = localStorage.getItem(VO_LANGUAGE_KEY(sheetId));
    if (v === 'es') return 'es';
  }
  return 'en';
};
export const setVoLanguage = (sheetId: string, lang: VoLanguage): void => {
  if (typeof window !== 'undefined' && sheetId) {
    localStorage.setItem(VO_LANGUAGE_KEY(sheetId), lang);
  }
};

// Default chart config (used when Chart Config sheet is not found)
export const DEFAULT_CHART_CONFIG = {
  chartName: 'Hot 100',
  horizontalTemplateId: 'x2m9UXiXciWjkiPHpMxE',
  verticalTemplateId: 'nAEJWKr0acvVNydAfkdC',
  voSubfolder: '',
  defaultOutroText: 'IF YOU WANT MORE BILLBOARD, MAKE SURE YOU HIT THE SUBSCRIBE BUTTON AND RING THE BELL TO BE NOTIFIED ON ALL OUR LATEST VIDEOS',
};

// Duration (seconds) of each chart's baked default-outro.wav. This is a
// property of the file we ship per voSubfolder, so it's keyed on voSubfolder
// (same key as the default-outro URL). Charts not listed use DEFAULT below.
// NOTE: when an upload-your-own-default feature is added later, replace the
// lookup body with the uploaded file's measured duration — every call site
// already routes through this function.
const DEFAULT_OUTRO_DURATION_BY_SUBFOLDER: { [voSubfolder: string]: number } = {
  'hot-latin-songs': 6.6,
};
const DEFAULT_OUTRO_DURATION_FALLBACK = 7;
export const getDefaultOutroDuration = (voSubfolder: string | undefined): number =>
  DEFAULT_OUTRO_DURATION_BY_SUBFOLDER[(voSubfolder || '').trim()] ?? DEFAULT_OUTRO_DURATION_FALLBACK;

// Canonical S3 URL for a chart's default outro audio, keyed on voSubfolder.
// Used by both the VO Manager preview and the export/render path so they share
// one source of truth (the S3 file). Keep the filename 'default-outro.wav' —
// the server's 0dB-gain special-case matches on it.
export const getDefaultOutroAudioUrl = (voSubfolder: string | undefined): string =>
  `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/VO/${voSubfolder || 'hot-100'}/default-outro.wav`;